// src/pages/onboarding/shared/steps/StepPasscodeConfirm.jsx
import React, { useMemo, useState } from "react";
import Button from "@/components/ui/Button";
import InputOtp from "@/sot/primitives/InputOtp";
import OnboardingSection from "@/components/onboarding/OnboardingSection";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supabase from "@/services/supabaseClient";
import { setMyPasscode } from "@/services/passcodeService.js";
import { useLocation } from "react-router-dom";
import { canUseOnboardingDraftMode } from "@/lib/onboardingDraftMode";

const LS_KEY = "afrimed_passcode_pending_v1";
const ALLOWED_PROFILE_TABLES = new Set(["patient_profiles", "doctor_profiles"]);

function readPendingPasscode() {
  try {
    const raw = sessionStorage.getItem(LS_KEY);
    const obj = raw ? JSON.parse(raw) : null;
    const d = String(obj?.digits || "").replace(/\D/g, "");
    return d.length === 6 ? d : "";
  } catch {
    return "";
  }
}

function clearPendingPasscode() {
  try {
    sessionStorage.removeItem(LS_KEY);
  } catch {}
}

function resolveProfileTable(passed, pathname) {
  if (typeof passed === "string" && ALLOWED_PROFILE_TABLES.has(passed)) return passed;
  if (String(pathname || "").includes("/onboarding/doctor")) return "doctor_profiles";
  if (String(pathname || "").includes("/onboarding/patient")) return "patient_profiles";
  return "patient_profiles";
}

export default function StepPasscodeConfirm({
  step,
  total,
  original,
  value,
  onChange,
  onBack,
  onContinue,
  profileTable: profileTableProp,
  contextNote = "",
  onTelemetry,
}) {
  const { refreshProfile } = useAuth() || {};
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const profileTable = useMemo(
    () => resolveProfileTable(profileTableProp, pathname),
    [profileTableProp, pathname]
  );

  const isControlled = typeof value !== "undefined" && typeof onChange === "function";
  const [local, setLocal] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    if (!isControlled) return;
    setLocal(value ?? "");
  }, [isControlled, value]);

  const val = isControlled ? (value ?? "") : local;
  const setVal = (next) => (isControlled ? onChange(next) : setLocal(next));

  const digits = useMemo(() => String(val || "").replace(/\D/g, ""), [val]);

  const originalDigits = useMemo(() => {
    const fromProp = String(original || "").replace(/\D/g, "");
    if (fromProp.length === 6) return fromProp;
    return readPendingPasscode();
  }, [original]);

  const valid =
    digits.length === 6 &&
    originalDigits.length === 6 &&
    digits === originalDigits;

  const handleContinue = async () => {
    onTelemetry?.("continue_clicked", { valid, saving });
    if (!valid) {
      onTelemetry?.("validation_blocked", { reason: "passcode_mismatch_or_incomplete" });
      return;
    }

    try {
      setSaving(true);
      setErr("");
      onTelemetry?.("save_started");

      if (!ALLOWED_PROFILE_TABLES.has(profileTable)) {
        throw new Error(`Invalid profile table: ${String(profileTable)}`);
      }

      const { data: auth, error: uErr } = await supabase.auth.getUser();
      if (uErr && !canUseOnboardingDraftMode()) throw uErr;
      if (!auth?.user?.id) {
        if (canUseOnboardingDraftMode()) {
          clearPendingPasscode();
          onContinue?.(digits);
          return;
        }
        throw new Error(
          t(
            "onboarding.passcodeConfirm.sessionExpired",
            "Session expired. Please log in again."
          )
        );
      }

      await setMyPasscode({
        profileTable,
        passcode: digits,
        userId: auth.user.id,
        role: profileTable === "doctor_profiles" ? "doctor" : "patient",
        onboardingStep: "step_passcode_confirm",
      });
      onTelemetry?.("save_succeeded");

      if (typeof refreshProfile === "function") {
        try {
          await refreshProfile();
        } catch {}
      }

      clearPendingPasscode();
      onContinue?.(digits);
    } catch (e) {
      onTelemetry?.("save_failed", { reason: "passcode_save_failed" });
      setErr(
        e?.message ||
          t("onboarding.passcodeConfirm.saveError", "Failed to save passcode")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <OnboardingTopBar step={step} total={total} onBack={onBack} />

      <OnboardingSection
        header={
          <div className="space-y-2">
            <h2 className="text-title-large text-on-surface">
              {t("onboarding.passcodeConfirm.title", "Pass code")}
            </h2>
            <p className="text-body-medium text-on-surface-var">
              {t(
                "onboarding.passcodeConfirm.subtitle",
                "Protect your account with a security code"
              )}
            </p>
            {contextNote ? (
              <p className="text-body-small text-on-surface-var">{contextNote}</p>
            ) : null}
          </div>
        }
        footer={
          <Button size="lg" disabled={!valid || saving} onClick={handleContinue}>
            {saving
              ? t("onboarding.passcodeConfirm.saving", "Saving...")
              : t("onboarding.passcodeConfirm.continue", "Continue")}
          </Button>
        }
      >
        <h3 className="text-label-md text-center mb-4">
          {t("onboarding.passcodeConfirm.confirm", "Confirm passcode")}
        </h3>

        <div className="flex justify-center">
          <div className="afrimed-otp">
            <InputOtp
              value={val}
              onChange={(next) => {
                onTelemetry?.("field_changed", { field: "passcode_confirm", value_recorded: false });
                setVal(next);
              }}
              length={6}
              tone="surface"
              type="password"
              inputMode="numeric"
              autoFocus
            />
          </div>
        </div>

        {digits.length === 6 && !valid ? (
          <p className="mt-4 text-center text-body-medium text-[rgb(var(--afrimed-danger))]">
            {t(
              "onboarding.passcodeConfirm.mismatch",
              "Passcodes do not match. Please try again."
            )}
          </p>
        ) : null}

        {err ? (
          <p className="mt-4 text-center text-body-medium text-[rgb(var(--afrimed-danger))]">
            {err}
          </p>
        ) : null}
      </OnboardingSection>
    </>
  );
}
